/* ─────────────────────────────────────────────────────────────
   R1 Discover · ROI Calculator
   Self-contained, no dependencies. Initializes on DOMContentLoaded.
   ───────────────────────────────────────────────────────────── */

(function () {
  "use strict";

  // ─── Constants ─────────────────────────────────────────────
  const SCENARIOS = {
    conservative: {
      label: "Conservative",
      blurb: "Heavily discounted assumptions for skeptical scrutiny.",
      realization: 0.30,
      billableCapture: 0.50,
      retentionLift: 0.02,
      workforceTurnoverReduction: 0.02,
    },
    realistic: {
      label: "Realistic",
      blurb: "Balanced view based on typical implementations.",
      realization: 0.50,
      billableCapture: 0.65,
      retentionLift: 0.04,
      workforceTurnoverReduction: 0.05,
    },
    optimistic: {
      label: "Optimistic",
      blurb: "Achievable with strong adoption and program fit.",
      realization: 0.70,
      billableCapture: 0.80,
      retentionLift: 0.07,
      workforceTurnoverReduction: 0.10,
    },
  };

  const DEFAULTS = {
    employees: 25,
    hourlySalary: 42,
    benefitsLoad: 25,
    workDays: 254,
    clientsPerYear: 500,
    ehrMinutesSaved: 8,
    assignmentMinutesSaved: 5,
    groupMinutesSaved: 8,
    sessionsPerWeek: 3,
    additionalEngagementsPerClient: 1.5,
    averageBillableRate: 80,
    revenuePerRetainedClient: 1200,
    currentTurnoverRate: 28,
    turnoverCostMultiplier: 0.5,
    annualR1Cost: 24000,
    implementationCost: 5000,
  };

  // ─── Formatters ────────────────────────────────────────────
  const fmtUSD = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
  const fmtCompact = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1,
  });
  const fmtNum = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
  const fmtNum2 = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

  // ─── Calculation ───────────────────────────────────────────
  function calculateRoi(inputs, scenarioKey) {
    const s = SCENARIOS[scenarioKey];
    const safe = {};
    for (const k in inputs) {
      safe[k] = Math.max(Number(inputs[k]) || 0, 0);
    }

    const loadedHourlyRate = safe.hourlySalary * (1 + safe.benefitsLoad / 100);
    const workWeeks = safe.workDays / 5;

    // Lever 1 — Realized labor value
    const docHours = (safe.employees * safe.ehrMinutesSaved * safe.workDays) / 60;
    const assignHours = (safe.employees * safe.assignmentMinutesSaved * safe.workDays) / 60;
    const groupHours = (safe.employees * safe.sessionsPerWeek * safe.groupMinutesSaved * workWeeks) / 60;
    const totalHoursSaved = docHours + assignHours + groupHours;
    const grossLaborValue = totalHoursSaved * loadedHourlyRate;
    const realizedLaborValue = grossLaborValue * s.realization;

    // Lever 2 — New billable engagements
    const additionalSessions = safe.clientsPerYear * safe.additionalEngagementsPerClient;
    const capturedSessions = additionalSessions * s.billableCapture;
    const billableValue = capturedSessions * safe.averageBillableRate;

    // Lever 3 — Client retention to next level of care
    const additionalRetained = safe.clientsPerYear * s.retentionLift;
    const retentionValue = additionalRetained * safe.revenuePerRetainedClient;

    // Lever 4 — Workforce retention
    const fewerDepartures = safe.employees * s.workforceTurnoverReduction;
    const annualLoadedSalary = loadedHourlyRate * 2080;
    const costPerDeparture = annualLoadedSalary * safe.turnoverCostMultiplier;
    const workforceValue = fewerDepartures * costPerDeparture;

    // Totals
    const totalGrossValue = realizedLaborValue + billableValue + retentionValue + workforceValue;
    const year1Investment = safe.annualR1Cost + safe.implementationCost;
    const steadyStateInvestment = safe.annualR1Cost;
    const year1Net = totalGrossValue - year1Investment;
    const steadyStateNet = totalGrossValue - steadyStateInvestment;
    const threeYearNet = year1Net + 2 * steadyStateNet;
    const roiMultiple = year1Investment > 0 ? totalGrossValue / year1Investment : 0;
    const paybackMonths = totalGrossValue > 0 ? (year1Investment / totalGrossValue) * 12 : 0;

    return {
      scenario: s,
      loadedHourlyRate,
      totalHoursSaved,
      grossLaborValue,
      realizedLaborValue,
      capturedSessions,
      billableValue,
      additionalRetained,
      retentionValue,
      fewerDepartures,
      costPerDeparture,
      workforceValue,
      totalGrossValue,
      year1Investment,
      steadyStateInvestment,
      year1Net,
      steadyStateNet,
      threeYearNet,
      roiMultiple,
      paybackMonths,
    };
  }

  // ─── State ─────────────────────────────────────────────────
  const state = {
    scenario: "realistic",
    inputs: Object.assign({}, DEFAULTS),
  };

  // ─── DOM helpers ───────────────────────────────────────────
  function $(id) { return document.getElementById(id); }
  function setText(id, value) {
    const el = $(id);
    if (el) el.textContent = value;
  }
  function setNetClass(id, value) {
    const el = $(id);
    if (!el) return;
    el.classList.remove("positive", "negative");
    el.classList.add(value >= 0 ? "positive" : "negative");
  }

  // ─── Render ────────────────────────────────────────────────
  function render() {
    const r = calculateRoi(state.inputs, state.scenario);
    const s = r.scenario;

    // Scenario blurb + dynamic text
    setText("roi-scenario-blurb", s.blurb);

    document.querySelectorAll('[data-scenario-text="realization-pct"]').forEach(function (el) {
      el.textContent = Math.round(s.realization * 100);
    });
    document.querySelectorAll('[data-scenario-text="capture-pct"]').forEach(function (el) {
      el.textContent = Math.round(s.billableCapture * 100);
    });
    document.querySelectorAll('[data-scenario-text="retention-pct"]').forEach(function (el) {
      el.textContent = (s.retentionLift * 100).toFixed(0);
    });
    document.querySelectorAll('[data-scenario-text="turnover-pct"]').forEach(function (el) {
      el.textContent = (s.workforceTurnoverReduction * 100).toFixed(0);
    });
    document.querySelectorAll('[data-scenario-text="label-lower"]').forEach(function (el) {
      el.textContent = s.label.toLowerCase();
    });

    // Headline metrics
    setText("roi-hl-net-value", fmtCompact.format(r.year1Net));
    setText("roi-hl-net-sub", "After " + fmtUSD.format(r.year1Investment) + " year-1 investment");
    setText("roi-hl-roi", r.roiMultiple.toFixed(1) + "×");
    setText("roi-hl-payback", (r.paybackMonths > 0 && r.paybackMonths < 60)
      ? r.paybackMonths.toFixed(1) + " mo"
      : "—");
    setText("roi-hl-3yr", fmtCompact.format(r.threeYearNet));
    setText("roi-hl-gross-value", fmtCompact.format(r.totalGrossValue));

    // Value bar
    const segments = [r.realizedLaborValue, r.billableValue, r.retentionValue, r.workforceValue];
    const total = segments.reduce(function (a, b) { return a + b; }, 0) || 1;
    const bar = $("roi-value-bar");
    if (bar) {
      const segs = bar.querySelectorAll(".roi-value-bar-segment");
      segments.forEach(function (v, i) {
        if (segs[i]) segs[i].style.width = ((v / total) * 100).toFixed(2) + "%";
      });
    }

    // Legend amounts
    setText("roi-legend-1", fmtCompact.format(r.realizedLaborValue));
    setText("roi-legend-2", fmtCompact.format(r.billableValue));
    setText("roi-legend-3", fmtCompact.format(r.retentionValue));
    setText("roi-legend-4", fmtCompact.format(r.workforceValue));

    // Lever — labor
    setText("roi-amt-labor", fmtUSD.format(r.realizedLaborValue));
    setText("roi-sub-labor",
      fmtNum.format(r.totalHoursSaved) + " hrs saved × " +
      fmtUSD.format(r.loadedHourlyRate) + "/hr × " +
      Math.round(s.realization * 100) + "% realization");
    setText("roi-secondary-labor", "Gross: " + fmtCompact.format(r.grossLaborValue));

    // Lever — billable
    setText("roi-amt-billable", fmtUSD.format(r.billableValue));
    setText("roi-sub-billable",
      fmtNum.format(r.capturedSessions) + " captured sessions × " +
      fmtUSD.format(state.inputs.averageBillableRate));

    // Lever — retention
    setText("roi-amt-retention", fmtUSD.format(r.retentionValue));
    setText("roi-sub-retention",
      fmtNum.format(r.additionalRetained) + " additional retained clients × " +
      fmtUSD.format(state.inputs.revenuePerRetainedClient));

    // Lever — workforce
    setText("roi-amt-workforce", fmtUSD.format(r.workforceValue));
    setText("roi-sub-workforce",
      fmtNum2.format(r.fewerDepartures) + " fewer departures × " +
      fmtCompact.format(r.costPerDeparture) + " replacement cost");

    // Investment
    setText("roi-amt-investment", "–" + fmtUSD.format(r.year1Investment));

    // Year 1 net total
    setText("roi-amt-net", fmtUSD.format(r.year1Net));
    setNetClass("roi-amt-net", r.year1Net);

    // 3-year projection
    setText("roi-y1-inv", fmtUSD.format(r.year1Investment));
    setText("roi-y1-net", fmtUSD.format(r.year1Net));
    setNetClass("roi-y1-net", r.year1Net);
    setText("roi-y2-inv", fmtUSD.format(r.steadyStateInvestment));
    setText("roi-y2-net", fmtUSD.format(r.steadyStateNet));
    setNetClass("roi-y2-net", r.steadyStateNet);
    setText("roi-y3-inv", fmtUSD.format(r.steadyStateInvestment));
    setText("roi-y3-net", fmtUSD.format(r.steadyStateNet));
    setNetClass("roi-y3-net", r.steadyStateNet);

    setText("roi-amt-3yr-cum", fmtUSD.format(r.threeYearNet));
    setNetClass("roi-amt-3yr-cum", r.threeYearNet);
  }

  // ─── Init ──────────────────────────────────────────────────
  function init() {
    // Populate input fields
    document.querySelectorAll("[data-input]").forEach(function (input) {
      const name = input.dataset.input;
      input.value = state.inputs[name];
      input.addEventListener("input", function () {
        const v = Math.max(Number(input.value) || 0, 0);
        state.inputs[name] = v;
        render();
      });
    });

    // Scenario toggle
    document.querySelectorAll(".roi-scenario-toggle button").forEach(function (btn) {
      btn.addEventListener("click", function () {
        state.scenario = btn.dataset.scenario;
        document.querySelectorAll(".roi-scenario-toggle button").forEach(function (b) {
          b.classList.remove("active");
        });
        btn.classList.add("active");
        render();
      });
    });

    // Reset
    const resetBtn = $("roi-reset-btn");
    if (resetBtn) {
      resetBtn.addEventListener("click", function () {
        state.inputs = Object.assign({}, DEFAULTS);
        document.querySelectorAll("[data-input]").forEach(function (input) {
          input.value = state.inputs[input.dataset.input];
        });
        render();
      });
    }

    render();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
